import { useState, useEffect } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  IconButton,
  Tooltip,
  Dialog,
  DialogContent,
  CircularProgress,
} from "@mui/material";
import { Info } from "@mui/icons-material";
import axios from "axios";
import { toast } from "react-toastify";
import { fetchToken } from "../../../../Auth";
import { adminURL } from "../../../../global";

export default function LinkedInSection({privileges}) {
  const {editPrivilege} = privileges
  const [raShorts, setRaShorts] = useState({
    0: "",
    1: "",
    2: "",
    3: "",
  });
  const [saveModal, setSaveModal] = useState(false);
  const [linkedInUrl, setLinkedInUrl] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [editMode, setEditMode] = useState(false);

  const getData = async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(`/fetch_ra_yt_link`, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${fetchToken("nuprice_access_token")}`,
        },
      });
      setRaShorts(response.data.raYTShorts || {});
      setLinkedInUrl(response.data.linkedInUrl || "");
    } catch (err) {
      console.error("Error", err);
      toast.error("Failed to fetch data");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    getData();
  }, []);

  const setValue = (id, value) => {
    if (id === "linkedIn") {
      setLinkedInUrl(value);
    } else {
      setRaShorts((prevState) => ({
        ...prevState,
        [id]: value,
      }));
    }
  };

  const handleSave = async () => {
    setIsLoading(true);
    const { 0: link1, 1: link2, 2: link3, 3: link4 } = raShorts;
    const trimmedLinkedInUrl = linkedInUrl.trim();

    if (
      !trimmedLinkedInUrl ||
      !trimmedLinkedInUrl.startsWith("https://www.linkedin.com/")
    ) {
      toast.error("Please fill a valid LinkedIn URL.");
      setIsLoading(false);
      return;
    }

    const trimmedLinks = [link1, link2, link3, link4].map((link) =>
      link.trim()
    );
    if (trimmedLinks.some((link) => !link)) {
      toast.error("Please fill all YouTube Shorts links.");
      setIsLoading(false);
      return;
    }

    const isValidEmbedLink = (link) => {
      return (
        link &&
        (link.startsWith("https://www.youtube.com/embed/") ||
          link.startsWith("https://www.linkedin.com/embed/"))
      );
    };

    if (trimmedLinks.some((link) => !isValidEmbedLink(link))) {
      toast.error("Please fill links with valid embedded URLs.");
      setIsLoading(false);
      return;
    }

    setSaveModal(true);
    const data = {
      raShorts: { ...raShorts, ...trimmedLinks },
      linkedIn: trimmedLinkedInUrl,
    };

    try {
      const response = await axios.post(`/edit_ra_yt_link`, data, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${fetchToken("nuprice_access_token")}`,
        },
      });
      if (response.status === 200 && response.data !== undefined) {
        toast.success("Details saved successfully");
        setEditMode(false);
        window.location.href = `/niyantran-panel/${adminURL}/website-content`;
      }
    } catch (err) {
      console.error("Error", err);
      if (err.response?.status === 400) {
        toast.error("This URL already exists! Please use a different URL");
      } else if (err.response?.status === 401) {
        window.location.href = "/login";
      } else {
        toast.error("Failed to save changes");
      }
    } finally {
      setSaveModal(false);
      setIsLoading(false);
    }
  };

  return (
    <Box sx={{ width: "100%", maxWidth: "100%" }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 4,
        }}
      >
        <Box>
          <Typography variant="h4" gutterBottom>
            Social Media
          </Typography>
          <Typography variant="subtitle1" color="text.secondary">
            Manage your LinkedIn embed and YouTube shorts
          </Typography>
        </Box>
        <Box>
          {editMode ? (
            <>
             <Button
                variant="contained"
                onClick={handleSave}
                disabled={isLoading}
                sx={{
                  mr: 1,
                  minWidth: "140px", // Set a fixed minimum width
                  position: "relative", // For absolute positioning of spinner
                }}
              >
                {isLoading && (
                  <CircularProgress
                    size={20}
                    sx={{
                      position: "absolute",
                      left: "10px",
                    }}
                  />
                )}
                Save Changes
              </Button>
              <Button
                variant="outlined"
                onClick={() => setEditMode(false)}
                disabled={isLoading}
              >
                Cancel
              </Button>
            </>
          ) : (
            editPrivilege && (
              <Button variant="outlined" onClick={() => setEditMode(true)}>
                Edit
              </Button>
            )
          )}
        </Box>
      </Box>

      <Box
        sx={{
          backgroundColor: "white",
          boxShadow: 1,
          borderRadius: 2,
          p: 3,
          mb: 4,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
          <Typography variant="h6" sx={{ mr: 1 }}>
            LinkedIn Embed URL
            <Typography component="span" color="error.main">
              *
            </Typography>
          </Typography>
          <Tooltip title="Go to LinkedIn and navigate to the post you want to embed. Click on more options and choose 'Embed this post'">
            <IconButton size="small">
              <Info fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
        <TextField
          fullWidth
          value={linkedInUrl}
          onChange={(e) => setValue("linkedIn", e.target.value)}
          placeholder="https://www.linkedin.com/embed/..."
          variant="outlined"
          disabled={!editMode}
        />
      </Box>

      <Box
        sx={{
          backgroundColor: "white",
          boxShadow: 1,
          borderRadius: 2,
          p: 3,
        }}
      >
        <Typography variant="h6" gutterBottom>
          YouTube Shorts Links
        </Typography>
        {[0, 1, 2, 3].map((index) => (
          <Box key={index} sx={{ mb: index !== 3 ? 4 : 0 }}>
            <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
              <Typography variant="subtitle1" sx={{ mr: 1 }}>
                Shorts Link - {index + 1}
                <Typography component="span" color="error.main">
                  *
                </Typography>
              </Typography>
              <Tooltip title="Go to Youtube Shorts and navigate to the post you want to embed. Click on more options and choose 'Embed'">
                <IconButton size="small">
                  <Info fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
            <TextField
              fullWidth
              value={raShorts[index]}
              onChange={(e) => setValue(index, e.target.value)}
              placeholder="https://www.youtube.com/embed/..."
              variant="outlined"
              disabled={!editMode}
            />
          </Box>
        ))}
      </Box>

      <Dialog open={saveModal} onClose={() => setSaveModal(false)}>
        <DialogContent sx={{ textAlign: "center", py: 4 }}>
          <CircularProgress sx={{ mb: 2 }} />
          <Typography variant="h6" gutterBottom>
            Saving changes...
          </Typography>
          <Typography color="text.secondary">
            This may take a few seconds
          </Typography>
        </DialogContent>
      </Dialog>
    </Box>
  );
}
